import { useState } from 'react';
import { Button, Checkbox, IconButton, Input, SectionLabel, Textarea } from '@wundo/ui';
import { CheckCheck, Plus, Trash2 } from 'lucide-react';
import type { MeetingActionItem } from '@wundo/shared/meetingPage.ts';
import { MeetingDoneActionItemsDialog } from './MeetingDoneActionItemsDialog.tsx';
import { m } from '@/paraglide/messages.js';

/** A row the user touched since this list was drawn, and how they left it. */
interface TouchedRow {
  item: MeetingActionItem;
  done: boolean;
}

export interface MeetingActionItemsProps {
  items: MeetingActionItem[];
  /**
   * What this list is. The preparation shows everything still open on the
   * conversation; the verslag shows what came out of this meeting — the same
   * component, two honest headings.
   */
  title: string;
  emptyLabel: string;
  /** The conversation these belong to, or null while the meeting has none. */
  threadId: string | null;
  /** How many of that conversation's agreements are already done. */
  doneCount: number;
  onToggle: (itemId: string, done: boolean) => void;
  onEdit: (itemId: string, text: string) => void;
  onAdd: (text: string) => void;
  onDelete: (itemId: string) => void;
}

/**
 * Agreements, listed and closable.
 *
 * A live view of the thread, not part of any document: ticking one off here, on
 * the preparation, or by telling Wundo in chat is the same act, and generation
 * never writes into this list.
 *
 * Closing one does not take it off the screen. The server hands back only what
 * is open, so the row would vanish under the finger that closed it — and a list
 * that empties itself reads as "gone", not as "done". A ticked line therefore
 * stays exactly where it was, struck through, until the page is opened again;
 * from then on it lives behind "afgerond", which is the other half of the same
 * promise: nothing a user closes is lost, it only moves out of the way.
 */
export function MeetingActionItems({
  items,
  title,
  emptyLabel,
  threadId,
  doneCount,
  onToggle,
  onEdit,
  onAdd,
  onDelete,
}: MeetingActionItemsProps) {
  const [adding, setAdding] = useState('');
  const [archiveOpen, setArchiveOpen] = useState(false);
  /*
   * What was ticked or unticked since this list was drawn. Cleared by nothing:
   * it lives as long as the component does, which is exactly "until the page is
   * opened again".
   */
  const [touched, setTouched] = useState<ReadonlyMap<string, TouchedRow>>(new Map());

  const submitNew = () => {
    const text = adding.trim();
    if (!text) return;
    onAdd(text);
    setAdding('');
  };

  const toggle = (item: MeetingActionItem, done: boolean) => {
    setTouched((was) => new Map(was).set(item.id, { item, done }));
    onToggle(item.id, done);
  };

  /*
   * Put one back from the archive. It has to leave the struck-through state
   * here too: the same agreement may have been closed a minute ago, in which
   * case this list is still holding it as done.
   */
  const reopen = (itemId: string) => {
    setTouched((was) => {
      const row = was.get(itemId);
      return row ? new Map(was).set(itemId, { item: row.item, done: false }) : was;
    });
    onToggle(itemId, false);
  };

  /* Deleting really is gone, so the row must not be held open by this map. */
  const remove = (itemId: string) => {
    setTouched((was) => {
      const next = new Map(was);
      next.delete(itemId);
      return next;
    });
    onDelete(itemId);
  };

  /*
   * The open list, plus the lines closed a moment ago, back in the order the
   * server would have given them — the same key it sorts on, so a ticked row
   * keeps the place it had rather than sliding to the end.
   */
  const openIds = new Set(items.map((item) => item.id));
  const rows = [
    ...items,
    ...[...touched.values()]
      .filter((row) => !openIds.has(row.item.id))
      .map((row) => row.item),
  ].sort(
    (one, other) =>
      one.created_at.localeCompare(other.created_at) || one.id.localeCompare(other.id),
  );

  const isDone = (itemId: string) => touched.get(itemId)?.done ?? false;

  return (
    <section>
      <SectionLabel as='h2'>{title}</SectionLabel>

      {rows.length === 0 && <p className='mt-2 text-base text-ink-muted'>{emptyLabel}</p>}

      {/* Square, not round: an action point is work somebody owes, not a topic to raise. */}
      <ul className='mt-1.5 flex flex-col'>
        {rows.map((item) => (
          <li
            key={item.id}
            className='group -mx-2 flex items-start gap-3 rounded-sm px-2 py-1.5 transition-colors hover:bg-fill-faint'
          >
            <Checkbox
              aria-label={
                isDone(item.id)
                  ? m.meeting_prep_actions_done_reopen({ text: item.text })
                  : m.meeting_prep_action_done({ text: item.text })
              }
              checked={isDone(item.id)}
              onCheckedChange={(checked) => toggle(item, checked === true)}
              className='mt-0.5'
            />
            {/*
              A textarea, not an input: generated action points run long, and a
              single-line field clips them. Enter commits the edit — an action
              point is one line of intent, not a paragraph.
            */}
            <Textarea
              variant='bare'
              rows={1}
              aria-label={m.meeting_prep_action_label()}
              defaultValue={item.text}
              className={isDone(item.id) ? 'text-ink-muted line-through' : ''}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                event.currentTarget.blur();
              }}
              onBlur={(event) => {
                const text = event.currentTarget.value.trim();
                if (text && text !== item.text) onEdit(item.id, text);
              }}
            />
            <IconButton
              size='sm'
              variant='danger'
              aria-label={m.meeting_prep_action_delete({ text: item.text })}
              className='-mt-0.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100'
              onClick={() => remove(item.id)}
            >
              <Trash2 className='size-4' aria-hidden='true' />
            </IconButton>
          </li>
        ))}
      </ul>

      <div className='mt-2 flex items-center gap-3'>
        <Input
          variant='bare'
          aria-label={m.meeting_prep_action_add_label()}
          placeholder={m.meeting_prep_action_add()}
          value={adding}
          onChange={(event) => setAdding(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            submitNew();
          }}
        />
        <Button
          size='sm'
          variant='ghost'
          disabled={adding.trim().length === 0}
          onClick={submitNew}
        >
          <Plus className='size-4' aria-hidden='true' />
          {m.meeting_prep_add_save()}
        </Button>
      </div>

      {/*
        Where everything closed went. One quiet line under the list, and only
        once there is something behind it: a door to an empty room is worse
        than no door.
      */}
      {threadId && doneCount > 0 && (
        <div className='mt-1 flex'>
          <Button size='sm' variant='ghost' onClick={() => setArchiveOpen(true)}>
            <CheckCheck className='size-4' aria-hidden='true' />
            {m.meeting_prep_actions_done_open({ count: doneCount })}
          </Button>
        </div>
      )}

      <MeetingDoneActionItemsDialog
        open={archiveOpen}
        threadId={threadId}
        onReopen={reopen}
        onClose={() => setArchiveOpen(false)}
      />
    </section>
  );
}
